import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {
  Button,
  Checkbox,
  Dialog,
  DialogFooter,
  Heading,
  Theme,
  ThemeName,
} from '@taopix/taopix-design-system';
import {
  AlternativeProductData,
  useChangeProductContext,
} from '../Context/ChangeProduct/ChangeProductContext';
import {useTaopix} from '../Context/TaopixContext';
import {ProductLayoutPricing} from '@taopix/taopix-core/dist/types/types';
import {useSelector} from 'react-redux';
import {CurrencyAdapter, Lang, Localisation} from '@taopix/taopix-core';
import useSettings from '../../Hooks/useSettings';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import {LayoutSelector, TotalPageCountSelector} from '../../Selectors';
import ProductPreviewPanel from './ProductPreviewPanel';
import {ChangeProductActions} from '../Context/ChangeProduct/ChangeProductActionTypes';

export type AlternativeProductPrices = {
  [key: string]: string;
};

export type ChangeProductPanelProps = {
  onSetSelectedProduct: (selectedProduct: any) => void;
  onChangeProduct: (ref: number) => void;
  alternativeProductData: AlternativeProductData[];
  selectedProduct: AlternativeProductData;
};

const ChangeProductPanel = ({
  onSetSelectedProduct,
  alternativeProductData,
  selectedProduct,
  onChangeProduct,
  ...props
}: ChangeProductPanelProps) => {
  const app = useTaopix();
  const ui = useUiState();
  const {t} = useTranslation();
  const settings = useSettings();

  const changeProduct = useChangeProductContext();
  const productLayout = useSelector(LayoutSelector);
  const pageCount = useSelector(TotalPageCountSelector);

  const showPreviewPanelAsDialog = ui.state.isSmallScreen;

  const serverURL = window.config.baseURL;
  const thumbnailPath = settings.state.urls.thumbnailPath;

  const currency = useMemo(
    () => CurrencyAdapter(settings.state.currency),
    [settings.state.currency.code]
  );

  const pricingEngine = app.core.get('pricingEngine');

  const calculateProjectPrice = (pricingModel: ProductLayoutPricing) => {
    const pricingEngineInstance = new pricingEngine(
      currency,
      1,
      pricingModel,
      productLayout.features.aimode
    );
    return pricingEngine.formatCurrencyNumber(
      pricingEngineInstance.calculateProjectPrice(pageCount, [], 0, 0),
      currency
    );
  };

  // Calculate the price for each of the alternative layouts
  const productPrices = useMemo(() => {
    return alternativeProductData.reduce((acc, curr) => {
      if (!acc[curr.code]) {
        const productPrice = calculateProjectPrice(curr.pricing);
        acc[curr.code] = productPrice;
      }
      return acc;
    }, {} as AlternativeProductPrices);
  }, [alternativeProductData, productLayout.code]);

  // src is always normal thumbnail image
  // if highdpi src is there add it to src set attr on img

  let highDpiThumbnailImage;

  const getImagePath = (product: AlternativeProductData) => {
    const thumbnailImage = product.thumbnailImage;
    if (product.highDPIThumbnailImage) {
      highDpiThumbnailImage = `${serverURL}/${thumbnailPath}/products/${productLayout.collectioncode}/${product.thumbPath}/${product.highDPIThumbnailImage} 2x`;
    }
    return `${serverURL}/${thumbnailPath}/products/${productLayout.collectioncode}/${product.thumbPath}/${thumbnailImage}`;
  };

  return (
    <div className={!showPreviewPanelAsDialog ? 'flex flex-row space-x-md h-full' : 'flex'}>
      <div className={'space-y-sm overflow-y-auto lg:w-[500rem] sm:w-full'}>
        {alternativeProductData &&
          alternativeProductData?.map((product, index) => {
            return (
              <Checkbox
                key={`product-${product?.ref}-${index}`}
                id={`product-${product?.ref}-${index}`}
                label={t('*:str_LabelAlternativeProduct', {
                  defaultValue: 'Alternative product',
                })}
                hideLabel
                appearance={'border'}
                className={'rounded-themeCornerSize'}
                defaultChecked={selectedProduct?.ref === product?.ref}
                preventManualUncheck
                onChange={() => {
                  onSetSelectedProduct(product);
                }}
              >
                <Theme name={ThemeName.Section} className={'flex space-x-sm overflow-hidden'}>
                  {product.thumbPath && '' !== product.thumbnailImage && (
                    // change product thumbnail image
                    <img
                      id={`product-image-${product.thumbPath}`}
                      className={'h-[100rem] w-[100rem] p-xs object-contain'}
                      src={getImagePath(product)}
                      alt={'Product preview thumbnail'}
                      srcSet={product.highDPIThumbnailImage ? highDpiThumbnailImage : undefined}
                    ></img>
                  )}
                  <div className={'mx-sm p-sm'}>
                    <Heading level={1} size={4}>
                      {Localisation.getLocaleString(Lang.getCode(), product.name)}
                    </Heading>
                    {settings.state.price.showPrice && <p>{productPrices[product.code]}</p>}
                  </div>
                  {showPreviewPanelAsDialog && (
                    <Button
                      id={`more-info-${product.ref}`}
                      className={'absolute bottom-20 right-20'}
                      label={t('*:str_LabelMoreInfoShort')}
                      size={'small'}
                      corners={'theme'}
                      buttonStyle={'tertiary'}
                      onClick={(event: any) => {
                        onSetSelectedProduct(product);
                        changeProduct.dispatch(
                          ChangeProductActions.setChangeProductDialogPreviewDialogOpen(true)
                        );
                      }}
                    />
                  )}
                </Theme>
              </Checkbox>
            );
          })}
      </div>
      {showPreviewPanelAsDialog ? (
        <Dialog
          id={'change-product-preview-panel'}
          open={changeProduct.state.changeProductPreviewDialogOpen}
          title={selectedProduct.name}
          onClose={() => {
            changeProduct.dispatch(
              ChangeProductActions.setChangeProductDialogPreviewDialogOpen(false)
            );
          }}
        >
          <ProductPreviewPanel
            selectedProduct={selectedProduct}
            currency={currency}
            showPreviewPanelAsDialog={showPreviewPanelAsDialog}
            onChangeProduct={onChangeProduct}
            isChangeProduct={true}
          />
          <DialogFooter isSmallScreen={ui.state.isSmallScreen}>
            <Button
              label={t('*:str_ButtonClose')}
              buttonStyle={'negative'}
              onClick={() => {
                changeProduct.dispatch(
                  ChangeProductActions.setChangeProductDialogPreviewDialogOpen(false)
                );
              }}
            />
            <Button
              label={t('*:str_ButtonChange')}
              buttonStyle={'primary'}
              onClick={() => {
                onChangeProduct(selectedProduct?.ref);
                changeProduct.dispatch(
                  ChangeProductActions.setChangeProductDialogPreviewDialogOpen(false)
                );
              }}
            />
          </DialogFooter>
        </Dialog>
      ) : (
        <ProductPreviewPanel
          onChangeProduct={onChangeProduct}
          selectedProduct={selectedProduct}
          currency={currency}
          showPreviewPanelAsDialog={showPreviewPanelAsDialog}
          isChangeProduct={true}
        />
      )}
    </div>
  );
};

export default ChangeProductPanel;
